import React, { useState, useEffect } from 'react';
import supabase from '../lib/supabase';
import Card from '../components/UI/Card';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import SafeIcon from '../common/SafeIcon';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import * as FiIcons from 'react-icons/fi';

const { FiMail, FiRefreshCw, FiChevronDown, FiAlertCircle } = FiIcons;

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_notifications_crm_2025')
        .select('*')
        .order('sent_at', { ascending: false });

      if (error) {
        if (error.code === '42P01') {
            toast.error('Notifications table not found. Please contact administrator.');
            setNotifications([]);
        } else {
            throw error;
        }
      } else {
        setNotifications(data || []);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast.error('Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpanded(expanded === id ? null : id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <SafeIcon icon={FiMail} className="h-8 w-8 mr-3 text-primary-600" />
          Email Notification Log
        </h1>
        <button onClick={loadNotifications} className="p-2 text-gray-500 hover:text-primary-600 rounded-full hover:bg-gray-100">
          <SafeIcon icon={FiRefreshCw} className="h-5 w-5" />
        </button>
      </div>
      
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-center space-x-2">
          <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-blue-600" />
          <p className="text-sm font-medium text-blue-800">
            This page shows a log of all simulated email notifications sent by the system. No actual emails are sent.
          </p>
        </div>
      </Card>

      {notifications.length === 0 ? (
        <Card className="p-12 text-center">
          <SafeIcon icon={FiMail} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications yet</h3>
          <p className="text-gray-500">
            Perform actions like creating users or assigning tasks to generate notifications.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {notifications.map((notification) => (
            <Card key={notification.id} className="p-4">
              <div className="flex items-start justify-between cursor-pointer" onClick={() => toggleExpand(notification.id)}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-500">To: <span className="text-gray-900">{notification.recipient}</span></p>
                  <p className="text-md font-semibold text-gray-900 truncate">{notification.subject}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {format(new Date(notification.sent_at), 'MMM dd, yyyy HH:mm')} • <span className="capitalize">{notification.notification_type?.replace(/_/g, ' ')}</span>
                  </p>
                </div>
                <div className="ml-4 flex items-center">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${notification.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {notification.status}
                    </span>
                    <SafeIcon icon={FiChevronDown} className={`h-5 w-5 ml-2 text-gray-400 transition-transform ${expanded === notification.id ? 'rotate-180' : ''}`} />
                </div>
              </div>
              {expanded === notification.id && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Email Body:</h4>
                  <pre className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap font-sans">
                    {notification.body}
                  </pre>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Notifications;